import type { CatalogueType } from '@/config/types'
import type { PublicationCode } from '@/scopes/common/domain/models'
import type { FiltersInQuickSearch } from '@/scopes/search/quick-search/domain/types'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, IconGlyph, ToggleButton, ToggleButtonGroup, Typography } from '@sunwebgroup/ui-components/uds'

import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { css, styled } from 'styled-components'

import { getPublicationCodeByCatalogueAndSiteName } from '@/scopes/common/domain/functions/publicationCodes'

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { getDefaultQuickSearchSelection } from '@/scopes/search/domain/policies/defaultFilters'
import { publicationCodeSelector } from '@/scopes/search/quick-search/components/selectors'
import { createQuickSearchToggleClickEvent } from '@/scopes/search/quick-search/domain/analytics'
import { getCatalogues, getExtraCatalogueIconUDS } from '@/scopes/search/quick-search/domain/policies/sites'
import { useQuickSearchSelector } from '@/scopes/search/quick-search/infrastructure/store/useQuickSearchSelector'

const ToggleContainer = styled.div`
  width: calc(100% - ${space('primitives_space_32')});
  display: flex;
  justify-content: center;
  position: absolute;
  top: 128px;
  left: 0;
  right: 0;
  margin: 0 auto;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    top: 157px;
    width: 100%;
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    top: 213px;
  }
`

const QuickSearchToggleGroup = styled(ToggleButtonGroup)`
  background-color: ${tok('primitives_color_neutral_50')};
  padding: ${space('primitives_space_2')};
  box-sizing: border-box;
  border-radius: ${space('primitives_space_12')};
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${space('primitives_space_2')} solid ${tok('primitives_color_neutral_100')};
  max-width: 480px;
  width: 100%;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${tokPx('primitives_space_48')};
    border-radius: 34px;
    max-width: unset;
    width: unset;
  }
`

const QuickSearchToggleItem = styled(ToggleButton)<{ width: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${tok('primitives_color_neutral_50')};
  border: none;
  border-radius: ${tokPx('primitives_space_8')};
  min-height: ${tokPx('primitives_space_48')};
  min-width: ${tokPx('primitives_space_64')};
  padding: ${space('primitives_space_6')} ${space('primitives_space_2')};
  gap: unset;
  ${({ width }) => css`
    width: ${width};
  `}

  text-decoration: none;

  > :nth-child(2) {
    font-size: 12px;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    font-size: ${tokPx('primitives_space_16')};
    min-height: 40px;
    border-radius: 34px;
    flex-direction: row;
    padding: 9px ${space('primitives_space_16')};
    width: unset;
    > * {
      &:nth-child(2) {
        font-size: ${tokPx('primitives_space_16')};
      }
    }

    ${Typography} {
      ${Typography.modVariant('body-m-regular-tight')}
    }
  }
`

const ToggleIcon = styled(Icon)`
  ${Icon.modSize(24)}
  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Icon.modSize(18)}
    margin: 0 ${space('primitives_space_6')} 0 0;
  }
`

interface CatalogueButtonProps {
  catalogue: CatalogueType
  onSwitch: (publicationCode: PublicationCode, selection: Partial<FiltersInQuickSearch>) => void
  width: string
}

interface QuickSearchToggleProps {
  onSwitch: (publicationCode: PublicationCode, selection: Partial<FiltersInQuickSearch>) => void
  isLoading: boolean
}

function CatalogueButton({ catalogue, onSwitch, width }: CatalogueButtonProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const catalogueIcons: Record<CatalogueType, IconGlyph> = { summer: 'Sun', winter: 'MountainsSlope' }

  const siteName = useConfiguration('siteName')
  const publicationCode = getPublicationCodeByCatalogueAndSiteName(catalogue, siteName)
  const selection = getDefaultQuickSearchSelection(publicationCode as PublicationCode)
  const clickHandler = () => {
    sendEvent(createQuickSearchToggleClickEvent(catalogue))
    onSwitch(publicationCode, selection)
  }
  return (
    <QuickSearchToggleItem width={width} value={publicationCode} key={publicationCode} onClick={clickHandler} color="neutral">
      <ToggleIcon glyph={catalogueIcons[catalogue]} />
      <Typography variant="body-xs-regular-tight">
        {intl.formatMessage({ id: `quicksearch_${catalogue}_catalogue_button` })}
      </Typography>
    </QuickSearchToggleItem>
  )
}

export default function QuickSearchToggle({ onSwitch, isLoading }: QuickSearchToggleProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const links = useConfiguration('links')
  const siteName = useConfiguration('siteName')
  const catalogues = getCatalogues(siteName)
  const extraCatalogueIcon = getExtraCatalogueIconUDS(siteName)
  const selectedPublicationCode = useQuickSearchSelector(publicationCodeSelector)

  const extraCatalogueClick = useCallback(() => {
    sendEvent(createQuickSearchToggleClickEvent())
    window.open(links.extraCatalogue, '_blank')
  }, [sendEvent, links])

  const itemsNo = extraCatalogueIcon ? catalogues.length + 1 : catalogues.length

  const renderCatalogueItems = useMemo(() => {
    const itemWidth = `calc(100% / ${itemsNo})`

    return (
      <>
        {catalogues.map((catalogue) => {
          return <CatalogueButton width={itemWidth} catalogue={catalogue} key={catalogue} onSwitch={onSwitch} />
        })}
        {extraCatalogueIcon && (
          <QuickSearchToggleItem width={`calc(100% / ${itemsNo})`} value="extra" onClick={extraCatalogueClick}>
            <ToggleIcon glyph={extraCatalogueIcon as IconGlyph} />
            <Typography>{intl.formatMessage({ id: `quicksearch_extra_catalogue_button` })}</Typography>
          </QuickSearchToggleItem>
        )}
      </>
    )
  }, [catalogues, extraCatalogueIcon, itemsNo])

  if (itemsNo < 2)
    return null

  return (
    <ToggleContainer data-qa="quick-search-catalogue-toggle">
      <QuickSearchToggleGroup disabled={isLoading} type="single" value={selectedPublicationCode}>
        {renderCatalogueItems}
      </QuickSearchToggleGroup>
    </ToggleContainer>
  )
}
